import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { autopilotStatus, autopilotSet } from "@/lib/api";
import { Robot, Pause, Play, Power, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtWhen = (dateStr, timeStr) => {
  if (!dateStr) return "—";
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
  const label = dateStr === today ? "Today" : dateStr === tomorrow ? "Tomorrow"
    : new Date(dateStr + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (!timeStr) return label;
  const [h, m] = timeStr.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${label} ${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function AutopilotStatusCard({ clientId }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: s } = useQuery({
    queryKey: ["autopilot-status", clientId],
    queryFn: () => autopilotStatus(clientId),
    enabled: !!clientId, refetchInterval: 60000,
  });
  const setState = useMutation({
    mutationFn: (state) => autopilotSet(clientId, state),
    onSuccess: (_d, state) => {
      qc.invalidateQueries({ queryKey: ["autopilot-status", clientId] });
      toast.success(state === "active" ? "Auto Pilot resumed" : state === "paused" ? "Auto Pilot paused" : "Auto Pilot disabled");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update Auto Pilot"),
  });
  if (!s) return null;

  const hasError = (s.failed_this_month || 0) > 0;
  const status = hasError && s.state === "active"
    ? { emoji: "🔴", label: "Publishing Error", cls: "text-red-400" }
    : s.state === "active"
      ? { emoji: "🟢", label: "Auto Pilot Active", cls: "text-emerald-400" }
      : s.state === "paused"
        ? { emoji: "⚪", label: "Paused", cls: "text-[#9a9a9a]" }
        : (s.pending_approval || 0) > 0
          ? { emoji: "🟡", label: "Waiting for Approval", cls: "text-amber-300" }
          : { emoji: "⚪", label: "Auto Pilot Off", cls: "text-[#9a9a9a]" };

  const failNotif = (s.notifications || []).find((n) => n.type === "publish_failed" && !n.read);

  return (
    <div className="card-matte p-5 md:p-6 mb-6 fade-up" data-testid="automation-status-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#6B3DFF]/12 flex items-center justify-center">
            <Robot size={20} className="brand-text" weight="duotone" />
          </div>
          <div>
            <div className="label-caps text-[10px]">Automation Status</div>
            <div className={`font-head font-bold ${status.cls}`} data-testid="automation-status-label">
              {status.emoji} {status.label}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {s.state === "active" && (
            <button data-testid="autopilot-pause-btn" onClick={() => setState.mutate("paused")}
              className="h-9 rounded-full px-4 flex items-center gap-1.5 text-xs font-semibold border border-white/10 text-[#cfcfcf] hover:text-white hover:bg-white/5">
              <Pause size={13} weight="fill" /> Pause
            </button>
          )}
          {s.state === "paused" && (
            <button data-testid="autopilot-resume-btn" onClick={() => setState.mutate("active")}
              className="h-9 rounded-full px-4 flex items-center gap-1.5 text-xs font-semibold border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
              <Play size={13} weight="fill" /> Resume
            </button>
          )}
          {s.state !== "off" && (
            <button data-testid="autopilot-disable-btn" onClick={() => setState.mutate("off")}
              className="h-9 rounded-full px-4 flex items-center gap-1.5 text-xs font-semibold border border-white/10 text-[#9a9a9a] hover:text-white hover:bg-white/5">
              <Power size={13} /> Disable
            </button>
          )}
          {s.state === "off" && s.plan_allows && (s.scheduled_this_month || 0) > 0 && (
            <button data-testid="autopilot-enable-btn" onClick={() => setState.mutate("active")}
              className="h-9 rounded-full px-4 flex items-center gap-1.5 text-xs font-semibold border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
              <Play size={13} weight="fill" /> Enable Auto Pilot
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <div className="rounded-xl bg-white/[0.03] border border-white/8 p-3" data-testid="autopilot-next-publish">
          <div className="label-caps text-[9px]">Next Publish</div>
          <div className="mt-1 text-sm font-semibold">{s.next_publish ? fmtWhen(s.next_publish.date, s.next_publish.time) : "—"}</div>
          {s.next_publish && (
            <div className="text-[10px] text-[#7a7a7a] truncate mt-0.5">
              {s.next_publish.platform}
              {s.next_publish.blocked_reason && <span className="text-amber-300"> · {s.next_publish.blocked_reason}</span>}
              {!s.next_publish.blocked_reason && s.next_publish.overdue && <span className="text-amber-300"> · due now</span>}
            </div>
          )}
        </div>
        <div className="rounded-xl bg-white/[0.03] border border-white/8 p-3" data-testid="autopilot-this-month">
          <div className="label-caps text-[9px]">This Month</div>
          <div className="mt-1 text-sm font-semibold">{s.scheduled_this_month || 0} scheduled</div>
          <div className="text-[10px] text-[#7a7a7a] mt-0.5">{s.remaining_this_month || 0} remaining</div>
        </div>
        <div className="rounded-xl bg-white/[0.03] border border-white/8 p-3" data-testid="autopilot-success-rate">
          <div className="label-caps text-[9px]">Success Rate</div>
          <div className={`mt-1 text-sm font-semibold ${s.success_rate == null ? "text-[#7a7a7a]" : hasError ? "text-amber-300" : "text-emerald-400"}`}>{s.success_rate == null ? "No data yet" : `${s.success_rate}%`}</div>
          <div className="text-[10px] text-[#7a7a7a] mt-0.5">{s.published_this_month || 0} published{s.failed_this_month ? ` · ${s.failed_this_month} failed` : ""}</div>
        </div>
        <div className="rounded-xl bg-white/[0.03] border border-white/8 p-3" data-testid="autopilot-last-publish">
          <div className="label-caps text-[9px]">Last Publish</div>
          <div className="mt-1 text-sm font-semibold">{s.last_publish?.platform || "—"}</div>
          {s.last_publish?.at && <div className="text-[10px] text-[#7a7a7a] mt-0.5">{fmtWhen(String(s.last_publish.at).slice(0, 10), String(s.last_publish.at).slice(11, 16))}</div>}
        </div>
      </div>

      {failNotif && (
        <button data-testid="autopilot-failure-alert" onClick={() => navigate("/approvals")}
          className="mt-3 w-full text-left rounded-xl border border-red-500/25 bg-red-500/[0.06] px-4 py-2.5 flex items-center gap-2.5 hover:bg-red-500/10">
          <WarningCircle size={16} className="text-red-400 shrink-0" weight="fill" />
          <span className="text-xs text-red-200 flex-1 truncate">{failNotif.title} — {failNotif.body}</span>
          <ArrowRight size={13} className="text-red-400 shrink-0" />
        </button>
      )}
    </div>
  );
}
